import { ChevronDown } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type {
  EnrichedOrderResponse,
  OrderSummaryResponse,
} from "@/features/order/types";
import {
  formatOrderReference,
  getOrderDisplayStatus,
} from "@/features/order/display";
import { formatBRL, formatDateBR } from "@/lib/timeAgo";

type OrderCardHeaderOrder = OrderSummaryResponse | EnrichedOrderResponse;

function StatusBadge({
  order,
  source = false,
}: {
  order: OrderCardHeaderOrder;
  source?: boolean;
}) {
  const { label, color } = getOrderDisplayStatus(order);
  const mobileLabel =
    order.status === "open"
      ? "Aberto"
      : order.status === "change_requested"
        ? "Alteração"
        : label;

  return (
    <span
      className={
        source
          ? `status ${order.status === "change_requested" ? "warning" : ["concluido", "encerrado", "cancelled"].includes(order.status) ? "muted" : ""}`
          : "order-card-header-status"
      }
      style={
        {
          "--order-status-color": color,
        } as CSSProperties
      }
    >
      {source ? (
        label
      ) : (
        <>
          <span className="order-card-header-status-full">{label}</span>
          <span className="order-card-header-status-mobile" aria-hidden>
            {mobileLabel}
          </span>
        </>
      )}
    </span>
  );
}

type OrderCardHeaderProps = {
  order: OrderCardHeaderOrder;
  isSuper: boolean;
  showClientName?: boolean;
  expanded: boolean;
  totalPrice?: number;
  onToggle: () => void;
  rowActions?: ReactNode;
};

export function OrderCardHeader({
  order,
  isSuper,
  showClientName = isSuper,
  expanded,
  totalPrice,
  onToggle,
  rowActions,
}: OrderCardHeaderProps) {
  const itemCount = "items" in order ? order.items.length : order.item_count;
  const identity = showClientName
    ? `${order.client_name}, ${order.store_name}`
    : order.store_name;
  const statusLabel = getOrderDisplayStatus(order).label;
  const formattedDate = formatDateBR(order.quotation_date);
  const hasTotal = Number.isFinite(totalPrice);
  const formattedTotal = hasTotal ? formatBRL(totalPrice!) : "";
  const accessibleTotal = formattedTotal.replace(/\u00a0/g, " ");
  const totalDescription = hasTotal ? `, total ${accessibleTotal}` : "";

  if (rowActions !== undefined) {
    const reference = formatOrderReference(order.id);
    return (
      <div
        className={`ledger-row ${isSuper ? "orders-grid" : "client-orders-grid"}`}
      >
        <div className="identity-cell">
          <span className="mobile-label">{isSuper ? "Pedido" : "Loja"}</span>
          <span className="identity-title-line">
            <span className="cell-title">
              {isSuper ? reference : order.store_name}
            </span>
            <span className="desktop-expander" aria-hidden="true">
              <ChevronDown
                size={15}
                style={{ transform: expanded ? "rotate(180deg)" : undefined }}
              />
            </span>
          </span>
          <span className="cell-sub">
            {isSuper
              ? `${itemCount} ${itemCount === 1 ? "item" : "itens"}`
              : `Pedido ${reference} · ${itemCount} ${itemCount === 1 ? "item" : "itens"}`}
          </span>
        </div>
        {showClientName && (
          <div>
            <span className="mobile-label">Cliente / loja</span>
            <span className="cell-title">{order.client_name}</span>
            <span className="cell-sub">{order.store_name}</span>
          </div>
        )}
        <div>
          <span className="mobile-label">Data</span>
          <span className="cell-title mono">{formattedDate}</span>
        </div>
        <div>
          <span className="mobile-label">Status</span>
          <StatusBadge order={order} source />
        </div>
        <div>
          <span className="mobile-label">Resumo</span>
          <span className="cell-title mono">{formattedTotal}</span>
          {order.pending_change_request_id && (
            <span className="cell-sub">Solicitação pendente</span>
          )}
        </div>
        {rowActions && <div className="row-actions">{rowActions}</div>}
        <span className="mobile-expander" aria-hidden="true">
          {expanded
            ? "Ocultar itens"
            : `Ver ${itemCount} ${itemCount === 1 ? "item" : "itens"}`}
          <ChevronDown size={14} />
        </span>
        <button
          type="button"
          className="row-open"
          data-testid={`order-card-toggle-${order.id}`}
          aria-label={`${expanded ? "Ocultar" : "Abrir"} pedido ${reference}`}
          aria-expanded={expanded}
          aria-controls={`order-card-panel-${order.id}`}
          onClick={onToggle}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="order-card-header"
      data-testid={`order-card-toggle-${order.id}`}
      data-has-total={hasTotal ? "true" : "false"}
      aria-label={`${expanded ? "Ocultar" : "Ver"} detalhes do pedido de ${identity} em ${formattedDate}, ${statusLabel}${totalDescription}`}
      aria-expanded={expanded}
      aria-controls={`order-card-panel-${order.id}`}
      onClick={onToggle}
    >
      <span className="order-card-header-identity">
        <span className="order-card-mobile-label">
          {showClientName ? "Pedido" : "Loja"}
        </span>
        <span className="order-card-header-names">
          <span
            className="order-card-header-reference"
            title={formatOrderReference(order.id)}
          >
            {showClientName ? formatOrderReference(order.id) : order.store_name}
          </span>
          <span
            className="order-card-header-chevron"
            data-testid="order-card-disclosure"
            data-expanded={expanded ? "true" : "false"}
            aria-hidden
          >
            <ChevronDown size={16} />
          </span>
        </span>
        <span className="order-card-header-identity-meta">
          {showClientName
            ? `${itemCount} ${itemCount === 1 ? "item" : "itens"}`
            : `${formatOrderReference(order.id)} · ${itemCount} ${itemCount === 1 ? "item" : "itens"}`}
        </span>
      </span>
      {showClientName && (
        <span className="order-card-header-party">
          <span className="order-card-mobile-label">Cliente / loja</span>
          <span className="order-card-header-client" title={order.client_name}>
            {order.client_name}
          </span>
          <span className="order-card-header-store" title={order.store_name}>
            {order.store_name}
          </span>
        </span>
      )}
      <span
        className="order-card-header-context"
        data-testid={`order-card-header-context-${order.id}`}
      >
        <span className="order-card-mobile-label">Data</span>
        <span className="order-card-header-date">{formattedDate}</span>
      </span>
      <span className="order-card-header-status-cell">
        <span className="order-card-mobile-label">Status</span>
        <StatusBadge order={order} />
      </span>
      {hasTotal && (
        <span className="order-card-header-total">
          <span className="order-card-mobile-label">Resumo</span>
          <span
            className="order-card-header-total-label"
            data-testid={`order-card-total-label-${order.id}`}
          >
            Total
          </span>
          <span
            className="order-card-header-total-value"
            data-testid={`order-card-total-${order.id}`}
          >
            {formattedTotal}
          </span>
        </span>
      )}
      <span className="order-card-header-action-space" aria-hidden />
    </button>
  );
}
